import EventService from "../../../../../service/event/EventService";

const isMessageEffectivelyEmpty = (html) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const text = tempDiv.textContent || tempDiv.innerText || "";
  return text.trim() === "";
};

const isValidLimit = (limit, label, context) => {
  if (limit?.enabled) {
    const value = Number(limit.value);
    if (limit.value === "" || isNaN(value) || value <= 0) {
      EventService.emit("showError", {
        message: `${label} in ${context} can't be empty.`,
      });
      return false;
    }
  }
  return true;
};

const validateWalletPayload = ({
  isCategoryRuleEnabled,
  categoryRuleData,
  isServiceRuleEnabled,
  serviceRuleData,
  isClaimRuleEnabled,
  claimRuleData,
  isWalletFaqsIncluded,
  walletFaqs,
  isUnlimitedWallet,
  walletLimit,
}) => {
  // 1. Validate categoryRuleData
  if (isCategoryRuleEnabled) {
    let allRulesEmpty = true;

    for (let i = 0; i < categoryRuleData.length; i++) {
      const item = categoryRuleData[i];
      const context = `Wallet Rule for category ID ${item.serviceCategoryId}`;

      const hasAnyEnabled =
        item.sublimit?.enabled ||
        item.perTransactionlimit?.enabled ||
        item.coPaylimit?.enabled;

      if (hasAnyEnabled) {
        allRulesEmpty = false;
      }

      if (
        !isValidLimit(item.sublimit, "Sublimit", context) ||
        !isValidLimit(
          item.perTransactionlimit,
          "Per Transaction Limit",
          context
        ) ||
        !isValidLimit(item.coPaylimit, "Copay Limit", context)
      ) {
        return false;
      }
    }

    if (allRulesEmpty) {
      EventService.emit("showError", {
        message: "No wallet rule added. At least one rule should be enabled.",
      });
      return false;
    }

    //------------------------------
    if (!isUnlimitedWallet) {
      let totalFixedAmountPercentage = 0;
      let totalFixedAmountValue = 0;

      categoryRuleData.forEach((item) => {
        if (item.isFixedAmount && item.sublimit?.enabled) {
          const value = Number(item.sublimit.value) || 0;

          if (item.sublimit.unit === 1) {
            totalFixedAmountPercentage += value;
          } else if (item.sublimit.unit === 2) {
            totalFixedAmountValue += value;
          }
        }
      });

      if (totalFixedAmountPercentage > 100) {
        EventService.emit("showError", {
          message:
            "Total sublimits (in %) across categories (Fixed) should not exceed 100%.",
        });
        return false;
      }

      const walletLimitNumeric = Number(walletLimit) || 0;
      if (totalFixedAmountValue > walletLimitNumeric) {
        EventService.emit("showError", {
          message:
            "Total sublimits (in amount) across categories (Fixed) should not exceed wallet limit.",
        });
        return false;
      }
    }
  }

  // 2. Validate serviceRuleData
  if (isServiceRuleEnabled) {
    let allServiceRulesEmpty = true;

    for (let i = 0; i < serviceRuleData.length; i++) {
      const item = serviceRuleData[i];

      if (item.serviceIds.length === 0) {
        EventService.emit("showError", {
          message: "Please fill - Health checkup service ID",
        });
        return false;
      }

      if (!item.serviceCardName?.trim()) {
        EventService.emit("showError", {
          message: "Please fill Transaction sublimit name",
        });
        return false;
      }

      const context = `Service Rule titled "${
        item.serviceCardName.trim() || `#${i + 1}`
      }"`;

      if (item.sublimit?.enabled) {
        allServiceRulesEmpty = false;
      }

      if (!isValidLimit(item.sublimit, "Sublimit", context)) {
        return false;
      }
    }

    if (allServiceRulesEmpty) {
      EventService.emit("showError", {
        message: "Please add Health checkup service sublimit.",
      });
      return false;
    }
  }

  // 3. Validate claimRuleData
  if (isClaimRuleEnabled) {
    if (claimRuleData.length === 0) {
      EventService.emit("showError", {
        message: `No advanced claim rule added.`,
      });
      return false;
    }

    const seenClaimTypes = new Set();

    for (let i = 0; i < claimRuleData.length; i++) {
      const item = claimRuleData[i];

      if (!item.claimType?.trim()) {
        EventService.emit("showError", {
          message: `Please fill claim type for claim rule #${i + 1}`,
        });
        return false;
      }

      const claimType = item.claimType.trim().toLowerCase();
      if (seenClaimTypes.has(claimType)) {
        EventService.emit("showError", {
          message: `Duplicate claim type "${
            item.claimType
          }" found in claim rule #${i + 1}`,
        });
        return false;
      }
      seenClaimTypes.add(claimType);

      const context = `Claim Rule for category ID ${
        item.serviceCategoryId || `#${i + 1}`
      }`;

      // Allow adding claim without filling any rule column
      // const hasAnyEnabled =
      //   item.sublimit?.enabled ||
      //   item.perTransactionlimit?.enabled ||
      //   item.coPaylimit?.enabled;

      // if (hasAnyEnabled === false) {
      //   EventService.emit("showError", {
      //     message: `Please fill advance claim rules for claim type ${item.claimType}`,
      //   });
      //   return false;
      // }

      if (
        !isValidLimit(item.sublimit, "Sublimit", context) ||
        !isValidLimit(
          item.perTransactionlimit,
          "Per Transaction Limit",
          context
        ) ||
        !isValidLimit(item.coPaylimit, "Copay Limit", context)
      ) {
        return false;
      }

      if (isMessageEffectivelyEmpty(item.instructions)) {
        EventService.emit("showError", {
          message: `Please fill instructions for claim type ${item.claimType}`,
        });
        return false;
      }
    }
  }

  // 4. Validate FAQs
  if (isWalletFaqsIncluded && walletFaqs.length === 0) {
    EventService.emit("showError", {
      message: "FAQ not added",
    });
    return false;
  }

  if (isWalletFaqsIncluded && walletFaqs.length > 0) {
    for (let i = 0; i < walletFaqs.length; i++) {
      const faq = walletFaqs[i];

      if (isMessageEffectivelyEmpty(faq.question)) {
        EventService.emit("showError", {
          message: `FAQ #${i + 1} Question is empty.`,
        });
        return false;
      }
      if (isMessageEffectivelyEmpty(faq.answer)) {
        EventService.emit("showError", {
          message: `FAQ #${i + 1} Answer is empty.`,
        });
        return false;
      }
    }
  }

  return true;
};

export default validateWalletPayload;
